import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import {
    ChangeDetectorRef,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    OnInit,
    Signal,
    signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Router } from '@angular/router';
import {
    DividerComponent,
    GroupingLogicComplexComponent,
    HttpErrorBaseComponent,
    InstructionsComponent,
} from '@r1-compass-library/components';
import { noopFormSaveMutationResult } from '@r1-compass-library/guards/hasUnsavedChanges.guard';
import {
    ApiError,
    CriteriaType,
    DataMartError,
    DataMartField,
    DataMartPicklistValue,
    DataMartQueryCountResponse,
    DataMartTable,
    DivisionConfig,
    ErrorSnackBarData,
    Play,
    PlayConfig,
    PlayCriteria,
    TemplateCriteria,
} from '@r1-compass-library/models';
import {
    convertSelectionCriterionForm,
    GenericSelectionCriterionForm,
    SelectionCriterionForm,
    SalesHistorySelectionCriterionDataForm,
} from '@r1-compass-library/models/criteria/selection-criteria-form.model';
import {
    SelectionCriterion,
    SelectionCriterionType,
} from '@r1-compass-library/models/criteria/selection-criteria.model';

import { DivisionCriteriaSourceSettings } from '@r1-compass-library/models/division/division-criteria-source-settings.model';
import { NarrowWideFormTypesPipe } from '@r1-compass-library/pipes/narrow-wide-form-types.pipe';
import { DataMartService } from '@r1-compass-library/services';
import { AssetStateMessageHandlingService } from '@r1-compass-library/services/asset-state-message-handling.service';
import { ErrorHandlingService } from '@r1-compass-library/services/error-handling.service';
import { DataMartModelsStore } from '@r1-compass-library/store/data-mart-models.store';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';
import { RequiredFields } from '@r1-compass-library/utilities/util-types';
import { filterNil } from 'ngxtension/filter-nil';
import { injectParams } from 'ngxtension/inject-params';
import {
    BehaviorSubject,
    catchError,
    combineLatest,
    filter,
    forkJoin,
    Observable,
    of,
    switchMap,
    take,
    tap,
} from 'rxjs';
import { DataMartTablesStore } from '../../../template-criteria/store/data-mart-tables.store';
import { TemplateCriteriaListStore } from '../../../template-criteria/store/template-criteria-list.store';
import { GenericSelectionCriterionComponent } from '../../../template-criteria/template-criteria-builder/criteria/generic-selection-criterion/generic-selection-criterion.component';
import { SalesHistoryCriterionComponent } from '../../../template-criteria/template-criteria-builder/criteria/sales-history-criterion/sales-history-criterion.component';
import { WebActivityCriterionComponent } from '../../../template-criteria/template-criteria-builder/criteria/web-activity-criterion/web-activity-criterion.component';
import { WebSearchCriterionComponent } from '../../../template-criteria/template-criteria-builder/criteria/web-search-criterion/web-search-criterion.component';
import { PlayStore } from '../../store/play.store';
import { PlayRecordFormService } from '../play-record-form.service';
import { PlayBuilderForm } from './play-builder-form.model';
import { QueryService } from './query.service';
import { SaveGuardStore } from '@r1-compass-library/guards/save-guard.store';
import { PlayRecordChildFormWarning } from '@r1-compass-library/guards/save-guards/play-record-selection.guard';
import { BuilderSaveGuardWarning } from '@r1-compass-library/guards/save-guards/builder-warning.interface';
import { PlayBuilderFormService } from './play-builder-form.service';
import { PlayValidationStateService } from '../../play-validation-state.service';
import { validTemplateCriteriaValidator } from '../../play-validators';

type RequiredFieldsErrorData = RequiredFields<ErrorSnackBarData, 'statusCode' | 'statusText' | 'messages'>;

@Component({
    selector: 'r1-compass-app-play-record-play-builder',
    imports: [
        DragDropModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatMenuModule,
        MatSelectModule,
        MatSlideToggleModule,
        ReactiveFormsModule,
        NarrowWideFormTypesPipe,
        DividerComponent,
        GenericSelectionCriterionComponent,
        GroupingLogicComplexComponent,
        InstructionsComponent,
        SalesHistoryCriterionComponent,
        WebActivityCriterionComponent,
        WebSearchCriterionComponent,
        HttpErrorBaseComponent,
    ],
    templateUrl: './play-record-play-builder.component.html',
    styleUrl: './play-record-play-builder.component.scss',
})
export class PlayRecordPlayBuilderComponent implements OnInit, PlayRecordChildFormWarning, BuilderSaveGuardWarning {
    readonly #templateCriteriaListStore = inject(TemplateCriteriaListStore);
    readonly #playStore = inject(PlayStore);
    readonly #tableStore = inject(DataMartTablesStore);
    readonly #queryService = inject(QueryService);
    readonly #dataMartService = inject(DataMartService);
    readonly #dataModelStore = inject(DataMartModelsStore);
    readonly #globalLoggedInUserStore = inject(GlobalLoggedInUserStore);
    readonly #changeDetectorRef = inject(ChangeDetectorRef);
    readonly #router = inject(Router);
    readonly #destroyRef$ = inject(DestroyRef);
    readonly #playRecordFormService = inject(PlayRecordFormService);
    readonly #errorHandlingService = inject(ErrorHandlingService);
    readonly #assetStateMessageHandlingService = inject(AssetStateMessageHandlingService);
    readonly #saveGuardStore = inject(SaveGuardStore);
    readonly #playValidationStateService = inject(PlayValidationStateService);

    readonly #playBuilderFormService = inject(PlayBuilderFormService);

    standardQuery = '';
    multipleRecommendationsQuery = '';
    createCount: number | null = null;
    updateCount: number | null = null;

    readonly #playId = injectParams('id');
    genericCriteriaTables: Signal<DataMartTable[] | undefined> = this.#dataModelStore.genericCriteriaTables;
    modelCriteriaTables = this.#dataModelStore.modelTables;
    loggedInUserId: Signal<number | undefined> = this.#globalLoggedInUserStore.selectedUserId;

    playBuilderForm: FormGroup<PlayBuilderForm> = this.#playRecordFormService.playBuilderForm;
    templateCriteriaFormArray: FormArray<FormGroup<SelectionCriterionForm>> = new FormArray([] as FormGroup[]);
    overrideToggleControl = this.#playBuilderFormService.overrideToggleControl;
    templateDescription = '';
    templateId: number | undefined = undefined;

    canDeactivate$ = new BehaviorSubject({
        dirtied: false,
        valid: this.playBuilderForm.valid,
    });

    private readonly canDeactivate = toSignal(this.canDeactivate$, { requireSync: true });
    // See `shouldDisableSave`'s service for context
    private doNotBlock = signal(false).asReadonly();

    public disableSave = computed<boolean>(() =>
        this.#playValidationStateService.shouldDisableSave(
            this.#playStore.play(),
            this.canDeactivate(),
            this.#playValidationStateService.invalidSections(),
            'recordSelection',
            this.doNotBlock()
        )
    );

    handleFormSaveWarningAfterInit() {
        this.#saveGuardStore.formUnsavedChangesWarningAfterNgOnInit({
            form: this.playBuilderForm,
            canDeactivate$: this.canDeactivate$,
            destroyRef$: this.#destroyRef$,
            disableSave: this.disableSave,
        });
    }

    protected overrideApiError = signal<RequiredFieldsErrorData | undefined>(undefined);
    protected queryApiError = signal<RequiredFieldsErrorData | undefined>(undefined);
    protected queryDatamartError = signal<RequiredFieldsErrorData | undefined>(undefined);
    protected hasQueryError = computed(
        () => this.queryApiError() !== undefined || this.queryDatamartError() !== undefined
    );
    protected hasOverrideError = computed(() => this.overrideApiError() !== undefined);

    private selectedDivisionConfig$ = toObservable(this.#globalLoggedInUserStore.selectedDivisionConfig);
    private selectedIamDivision$ = toObservable(this.#globalLoggedInUserStore.selectedIamDivision);

    divisionConfig: Signal<DivisionConfig | undefined> = this.#globalLoggedInUserStore.selectedDivisionConfig;
    allowOverrideQueries = computed(() => this.divisionConfig()?.config?.mapping?.allowOverrideQueries);
    divisionCriteriaSourceSettings: Signal<DivisionCriteriaSourceSettings | undefined> = computed(
        () => this.divisionConfig()?.config?.criteriaSourceSettings
    );
    protected multipleRecommendationsEnabled = computed(
        () => this.divisionConfig()?.config?.mapping?.multipleRecommendations?.enabled
    );
    private multipleRecommendationsEnabled$ = toObservable(this.multipleRecommendationsEnabled);

    salesHistoryCmtLevels = toSignal(
        this.#dataMartService.getSalesHistoryTargetLevels(
            this.divisionCriteriaSourceSettings()?.salesHistoryTable,
            this.divisionCriteriaSourceSettings()?.salesHistoryCmtColumn
        ),
        { initialValue: [] }
    );
    selfManufacturedOptions: Signal<DataMartPicklistValue[]> = toSignal(
        this.#dataMartService.getSelfManufacturedOptions(
            this.divisionCriteriaSourceSettings()?.salesHistoryTable,
            this.divisionCriteriaSourceSettings()?.salesHistorySelfMfgColumn
        ),
        { initialValue: [] }
    );
    sspOptions = toSignal(
        this.#dataMartService.getSspOptions(
            this.divisionCriteriaSourceSettings()?.salesHistoryTable,
            this.divisionCriteriaSourceSettings()?.salesHistorySspColumn
        ),
        { initialValue: [] }
    );
    privateLabelOptions = toSignal(
        this.#dataMartService.getPrivateLabelOptions(
            this.divisionCriteriaSourceSettings()?.salesHistoryTable,
            this.divisionCriteriaSourceSettings()?.salesHistoryProductLabelColumn
        ),
        { initialValue: [] }
    );
    salesHistoryChannels = toSignal(
        this.#dataMartService.getSalesHistoryChannels(
            this.divisionCriteriaSourceSettings()?.salesHistoryTable,
            this.divisionCriteriaSourceSettings()?.salesHistoryChannelsColumn
        ),
        { initialValue: [] }
    );
    salesHistoryMetricFields = toSignal(
        this.#dataMartService.getSalesHistoryMetrics(this.divisionCriteriaSourceSettings()?.salesHistoryTable),
        { initialValue: [] }
    );
    webActivityCmtLevels = toSignal(
        this.#dataMartService.getWebActivityTargetLevels(
            this.divisionCriteriaSourceSettings()?.webActivityTable,
            this.divisionCriteriaSourceSettings()?.webActivityCmtColumn
        ),
        { initialValue: [] }
    );
    webActivityPeriodFields = toSignal(
        this.#dataMartService.getWebActivityPeriods(this.divisionCriteriaSourceSettings()?.webActivityTable),
        { initialValue: [] }
    );
    protected templates = computed<TemplateCriteria[]>(() => {
        return this.#templateCriteriaListStore
            .vm()
            .assets.sort(function (a, b) {
                return a.name!.localeCompare(b.name!);
            })
            .filter(criteria => criteria.enabled && criteria.valid);
    });
    private templateCriteriaAssets = computed<TemplateCriteria[]>(() => this.#templateCriteriaListStore.vm().assets);
    private selectedTemplateCriteriaId = toSignal(this.playBuilderForm.controls.templateCriteriaId.valueChanges, {
        initialValue: this.playBuilderForm.controls.templateCriteriaId.value,
    });
    protected selectedInvalidTemplateCriteria = computed<TemplateCriteria | undefined>(() => {
        const selectedId = this.selectedTemplateCriteriaId();
        return this.templateCriteriaAssets().find(
            criteria => criteria.id === selectedId && (!criteria.enabled || !criteria.valid)
        );
    });
    allowedSelectionCriteria = computed<CriteriaType[]>(
        () => this.divisionConfig()?.config?.mapping?.allowedCriteria ?? []
    );

    constructor() {
        effect(
            () => {
                this.playBuilderForm.controls.templateCriteriaId.addValidators(
                    validTemplateCriteriaValidator(() => this.templateCriteriaAssets())
                );
                this.playBuilderForm.controls.templateCriteriaId.updateValueAndValidity();
            },
            { debugName: 'update template criteria validator on template criteria list change' }
        );
    }

    ngOnInit() {
        this.#dataModelStore.getDataModel();
        combineLatest([
            this.#playStore.play$.pipe(filterNil()),
            this.#templateCriteriaListStore.vm$.pipe(filter(res => res.assets.length > 0)),
            this.multipleRecommendationsEnabled$,
        ])
            .pipe(take(1))
            .subscribe(([play, _, multipleRecommendationsEnabled]) => {
                this.#playBuilderFormService.populatePlayBuilderForm(
                    this.playBuilderForm,
                    this.overrideToggleControl,
                    play,
                    multipleRecommendationsEnabled
                );
                this.onTemplateCriteriaSelection(play.templateCriteriaId);
                this.handleFormSaveWarningAfterInit();
            });
        this.selectedDivisionConfig$.pipe(filterNil()).subscribe(division => {
            const tableName = division.config?.mapping?.dataMartTable;
            if (tableName) {
                this.#tableStore.getTable({ tableName: tableName });
            }
        });

        const playId = this.#playId();
        if (playId && playId !== 'new' && this.#playStore.play()?.id !== +playId) {
            this.#playStore.getPlay({ id: +playId });
        }
        this.#templateCriteriaListStore.searchAssets({
            iamDivisionId: this.#globalLoggedInUserStore.selectedIamDivision()?.id,
        });
        this.#playStore.message$.pipe(filterNil()).subscribe(message => {
            this.#assetStateMessageHandlingService.handleMessage(message);
            this.#playStore.resetMessage();
        });
    }

    onTemplateCriteriaSelection(templateId?: number) {
        this.templateCriteriaFormArray.clear();
        this.#changeDetectorRef.detectChanges();
        const selectedTemplate = this.#templateCriteriaListStore
            .vm()
            .assets.find(template => +template.id === templateId);
        this.templateDescription = selectedTemplate?.description ?? '';
        this.templateId = selectedTemplate?.id ?? undefined;
        this.populateTemplateCriteriaFormArray(selectedTemplate);
    }

    private populateTemplateCriteriaFormArray(selectedTemplate?: TemplateCriteria) {
        this.templateCriteriaFormArray.patchValue(new FormArray<FormGroup<SelectionCriterionForm>>([]).getRawValue());
        selectedTemplate?.config.selectionCriteria?.forEach(criterion => {
            this.templateCriteriaFormArray.push(new FormGroup(new SelectionCriterionForm(criterion.type, criterion)));
        });
    }

    private populatePlayBuilderForm(play: Play) {
        play.config?.criteria?.selectionCriteria.forEach(criterion => {
            this.playBuilderForm.controls.selectionCriteria.push(
                new FormGroup(new SelectionCriterionForm(criterion.type, criterion))
            );
        });
        this.playBuilderForm.patchValue(
            new FormGroup(new PlayBuilderForm(play.templateCriteriaId, play.config?.criteria)).getRawValue()
        );
        if (this.multipleRecommendationsEnabled()) {
            if (this.playBuilderForm.controls.overrideMultipleRecommendationsQuery.value) {
                this.overrideToggleControl.patchValue(true);
                this.playBuilderForm.controls.overrideMultipleRecommendationsQuery.addValidators(Validators.required);
                this.playBuilderForm.controls.overrideMultipleRecommendationsQuery.updateValueAndValidity();
            } else {
                this.playBuilderForm.controls.overrideMultipleRecommendationsQuery.disable();
            }
            this.playBuilderForm.controls.overrideStandardQuery.disable();
        } else {
            if (this.playBuilderForm.controls.overrideStandardQuery.value) {
                this.overrideToggleControl.patchValue(true);
                this.playBuilderForm.controls.overrideStandardQuery.addValidators(Validators.required);
                this.playBuilderForm.controls.overrideStandardQuery.updateValueAndValidity();
            } else {
                this.playBuilderForm.controls.overrideStandardQuery.disable();
            }
            this.playBuilderForm.controls.overrideMultipleRecommendationsQuery.disable();
        }
    }

    onAddCriteria(criteriaType: SelectionCriterionType) {
        const criterionFormGroup = new FormGroup(new SelectionCriterionForm(criteriaType));
        if (criteriaType === 'sales history') {
            const availableChannels = this.salesHistoryChannels().map(channel => channel.value);
            (
                criterionFormGroup.controls.data as FormGroup<SalesHistorySelectionCriterionDataForm>
            ).controls.channel.setValue(availableChannels);
        }
        this.playBuilderForm.controls.selectionCriteria.push(criterionFormGroup);
        this.playBuilderForm.controls.selectionCriteria.markAllAsDirty();
        this.playBuilderForm.markAllAsDirty();
        this.playBuilderForm.updateValueAndValidity();
        this.playBuilderForm.markAllAsTouched();
    }

    onDeleteCriterion(index: number) {
        this.playBuilderForm.controls.selectionCriteria.removeAt(index);
        // Form arrays being programatically updated do not mark dirty automatically
        this.playBuilderForm.controls.selectionCriteria.markAllAsDirty();
        this.playBuilderForm.markAllAsDirty();
        this.playBuilderForm.updateValueAndValidity();
    }

    onOverrideQueryToggled(change: MatSlideToggleChange) {
        this.playBuilderForm.controls.overrideStandardQuery.reset();
        this.playBuilderForm.controls.overrideMultipleRecommendationsQuery.reset();
        const play = this.#playStore.play();

        if (!change.checked) {
            this.playBuilderForm.controls.overrideStandardQuery.removeValidators(Validators.required);
            this.playBuilderForm.controls.overrideStandardQuery.updateValueAndValidity();
            this.playBuilderForm.controls.overrideStandardQuery.disable();
            this.playBuilderForm.controls.overrideMultipleRecommendationsQuery.removeValidators(Validators.required);
            this.playBuilderForm.controls.overrideMultipleRecommendationsQuery.updateValueAndValidity();
            this.playBuilderForm.controls.overrideMultipleRecommendationsQuery.disable();
        } else if (this.multipleRecommendationsEnabled()) {
            this.playBuilderForm.controls.overrideMultipleRecommendationsQuery.addValidators(Validators.required);
            this.playBuilderForm.controls.overrideMultipleRecommendationsQuery.updateValueAndValidity();
            this.playBuilderForm.controls.overrideMultipleRecommendationsQuery.enable();

            if (play) {
                this.overrideApiError.set(undefined);
                const playToQuery: Play = {
                    ...play,
                    config: {
                        ...play.config,
                        criteria: this.convertFormToPlayCriteria(),
                    },
                };

                this.#queryService
                    .getSegmentationQuery(playToQuery, this.templateId)
                    .pipe(
                        catchError((err: ApiError) => {
                            const errorData = this.#errorHandlingService.displayApiError({ error: err, type: 'embed' });
                            if (errorData) {
                                this.overrideApiError.set(errorData);
                            }
                            return of(null);
                        }),
                        filterNil()
                    )
                    .subscribe(queryResult => {
                        this.playBuilderForm.controls.overrideMultipleRecommendationsQuery.setValue(
                            queryResult.multipleRecommendationsQuery
                        );
                    });
            }
        } else {
            this.playBuilderForm.controls.overrideStandardQuery.addValidators(Validators.required);
            this.playBuilderForm.controls.overrideStandardQuery.updateValueAndValidity();
            this.playBuilderForm.controls.overrideStandardQuery.enable();

            if (play) {
                this.overrideApiError.set(undefined);
                const playToQuery: Play = {
                    ...play,
                    config: {
                        ...play.config,
                        criteria: this.convertFormToPlayCriteria(),
                    },
                };

                this.#queryService
                    .getSegmentationQuery(playToQuery, this.templateId)
                    .pipe(
                        catchError((err: ApiError) => {
                            const errorData = this.#errorHandlingService.displayApiError({ error: err, type: 'embed' });
                            if (errorData) {
                                this.overrideApiError.set(errorData);
                            }
                            return of(null);
                        }),
                        filterNil()
                    )
                    .subscribe(queryResult =>
                        this.playBuilderForm.controls.overrideStandardQuery.setValue(queryResult.standardQuery)
                    );
            }
        }
    }

    onRunQuery() {
        const play = this.#playStore.play();
        if (play) {
            this.resetQueriesAndErrors();
            const playToQuery: Play = {
                ...play,
                config: {
                    ...play.config,
                    criteria: this.convertFormToPlayCriteria(),
                },
            };

            const updateQueries$ = this.#queryService.getCountsQuery(playToQuery, this.templateId).pipe(
                // Query counts
                tap(queryResult => {
                    this.standardQuery = queryResult.standardQuery;
                    this.multipleRecommendationsQuery = queryResult.multipleRecommendationsQuery;
                }),
                catchError((err: ApiError) => {
                    const errorData = this.#errorHandlingService.displayApiError({ error: err, type: 'embed' });

                    if (errorData) {
                        this.queryApiError.set(errorData);
                    }
                    return of(null);
                }),
                // Update counts
                switchMap(() => {
                    if (this.multipleRecommendationsEnabled()) {
                        return forkJoin(this.buildMultipleRecommendationsQueryRequests()).pipe(
                            tap(res => {
                                this.createCount = res[0].createCount;
                                this.updateCount = res[0].updateCount;
                            })
                        );
                    } else {
                        return forkJoin(this.buildSingleQueryRequests()).pipe(
                            tap(res => {
                                this.createCount = res[0].createCount;
                                this.updateCount = res[0].updateCount;
                            })
                        );
                    }
                }),
                catchError((err: DataMartError) => {
                    const errorData = this.#errorHandlingService.displayDataMartApiError({ error: err, type: 'embed' });

                    if (errorData) {
                        this.queryDatamartError.set(errorData);
                    }

                    return of(null);
                })
            );

            updateQueries$.subscribe();
        }
    }

    onSave() {
        const play = this.#playStore.play();
        if (play) {
            play.valid = this.#playValidationStateService.determinePlayValidity('recordSelection', play);

            const playToSave: Play = {
                ...play,
                //we strip out the status because we don't want to update it with stale data
                status: undefined,
                templateCriteriaId: this.playBuilderForm.controls.templateCriteriaId.value,
                updatedBy: this.loggedInUserId()!,
                lastConfigured: new Date(),
            };
            const config: Partial<PlayConfig> = { criteria: this.convertFormToPlayCriteria() };
            return this.#playStore.savePlayAndData({ play: playToSave, config: config });
        } else {
            return noopFormSaveMutationResult();
        }
    }

    drop(event: CdkDragDrop<FormGroup[]>) {
        moveItemInArray(
            this.playBuilderForm.controls.selectionCriteria.controls ?? [],
            event.previousIndex,
            event.currentIndex
        );
    }

    routeToTemplateCriteria() {
        const url = this.#router.serializeUrl(
            this.#router.createUrlTree(['/template-criteria', this.templateId, 'definition'])
        );
        window.open(url);
    }

    private buildMultipleRecommendationsQueryRequests(): Observable<DataMartQueryCountResponse> {
        return this.#dataMartService.getMultipleRecommendationsQueryCount(this.multipleRecommendationsQuery);
    }

    private buildSingleQueryRequests(): Observable<DataMartQueryCountResponse> {
        return this.#dataMartService.getQueryCount(this.standardQuery);
    }

    private convertFormToPlayCriteria(): PlayCriteria {
        const selectionCriteria: SelectionCriterion[] = [];
        this.playBuilderForm.controls.selectionCriteria?.controls.forEach(criterionForm => {
            let fields = [] as DataMartField[];
            if (criterionForm.controls.type.value === 'generic') {
                const data = criterionForm.controls.data as FormGroup<GenericSelectionCriterionForm>;
                const table = this.genericCriteriaTables()?.find(tbl => tbl.name === data.controls.tableName.value);
                fields = table?.fields ?? [];
            } else if (criterionForm.controls.type.value === 'model') {
                const data = criterionForm.controls.data as FormGroup<GenericSelectionCriterionForm>;
                const table = this.modelCriteriaTables()?.find(tbl => tbl.name === data.controls.tableName.value);
                fields = table?.fields ?? [];
            }
            selectionCriteria.push(convertSelectionCriterionForm(criterionForm, fields));
        });
        return {
            selectionCriteria: selectionCriteria,
            groupingLogic: this.playBuilderForm.controls.groupingLogic.getRawValue(),
            overrideStandardQuery: this.playBuilderForm.controls.overrideStandardQuery.getRawValue(),
            overrideMultipleRecommendationsQuery:
                this.playBuilderForm.controls.overrideMultipleRecommendationsQuery.getRawValue(),
        };
    }

    private resetQueriesAndErrors() {
        this.createCount = null;
        this.updateCount = null;
        this.standardQuery = '';
        this.multipleRecommendationsQuery = '';

        this.queryApiError.set(undefined);
        this.queryDatamartError.set(undefined);
    }
}
